/**
 * RealtimeSession — WebRTC connection to Azure OpenAI Realtime API.
 *
 * Adapted from ai-canvas useWebRTC.ts + useToolDataChannel.ts.
 * Handles: mic capture, SDP exchange, data channel messaging,
 * and remote audio track reception.
 */

const ICE_SERVERS: RTCIceServer[] = [{ urls: 'stun:stun.l.google.com:19302' }]
const ICE_GATHER_TIMEOUT_MS = 3000
const DATA_CHANNEL_NAME = 'oai-events'

export interface RealtimeTool {
  type: 'function'
  name: string
  description: string
  parameters: object
}

export interface RealtimeSessionOptions {
  ephemeralKey: string
  webrtcUrl: string
  model?: string
  voice?: string
  instructions?: string
  tools?: RealtimeTool[]
}

export class RealtimeSession {
  // --- Callbacks ---
  onConnected: (() => void) | null = null
  onDisconnected: (() => void) | null = null
  onRemoteTrack: ((stream: MediaStream) => void) | null = null
  onDataMessage: ((msg: any) => void) | null = null
  onError: ((err: Error) => void) | null = null

  // --- Internal state ---
  private pc: RTCPeerConnection | null = null
  private dc: RTCDataChannel | null = null
  private micStream: MediaStream | null = null
  private _remoteAudioStream: MediaStream | null = null
  private _connected = false
  private opts: Required<RealtimeSessionOptions>

  constructor(opts: RealtimeSessionOptions) {
    this.opts = {
      model: 'gpt-4o-realtime-preview',
      voice: 'verse',
      instructions: '',
      tools: [],
      ...opts,
    }
  }

  get isConnected(): boolean {
    return this._connected
  }

  get dataChannel(): RTCDataChannel | null {
    return this.dc
  }

  get remoteAudioStream(): MediaStream | null {
    return this._remoteAudioStream
  }

  // -----------------------------------------------------------------------
  // Public API
  // -----------------------------------------------------------------------

  /** Full connection flow: mic → PC → data channel → SDP exchange → connected. */
  async connect(): Promise<void> {
    try {
      // 1. Request microphone
      console.log('[RealtimeSession] Requesting microphone...')
      this.micStream = await navigator.mediaDevices.getUserMedia({ audio: true })
      console.log('[RealtimeSession] Microphone granted')

      // 2. Create peer connection
      this.pc = new RTCPeerConnection({ iceServers: ICE_SERVERS })
      this.setupPeerConnectionHandlers()

      // 3. Add mic track
      for (const track of this.micStream.getAudioTracks()) {
        this.pc.addTrack(track, this.micStream)
      }

      // 4. Create data channel BEFORE SDP offer (required by WebRTC spec)
      this.dc = this.pc.createDataChannel(DATA_CHANNEL_NAME)
      this.setupDataChannelHandlers()

      // 5. Create SDP offer and wait for ICE gathering
      const offer = await this.pc.createOffer()
      await this.pc.setLocalDescription(offer)
      await this.waitForIceGathering()

      const localSdp = this.pc.localDescription?.sdp
      if (!localSdp) {
        throw new Error('No local SDP after ICE gathering')
      }

      // 6. Exchange SDP with Azure Realtime endpoint
      console.log('[RealtimeSession] Exchanging SDP...')
      const answerSdp = await this.exchangeSDP(localSdp)

      // 7. Set remote description
      await this.pc.setRemoteDescription({ type: 'answer', sdp: answerSdp })
      console.log('[RealtimeSession] Remote description set')

    } catch (err) {
      const error = err instanceof Error ? err : new Error(String(err))
      console.error('[RealtimeSession] Connection failed:', error.message)
      this.onError?.(error)
      this.close()
      throw error
    }
  }

  /** Send a JSON message over the data channel. */
  sendJSON(msg: object): void {
    if (!this.dc || this.dc.readyState !== 'open') {
      console.warn('[RealtimeSession] Data channel not open, cannot send:', msg)
      return
    }
    this.dc.send(JSON.stringify(msg))
  }

  /** Close the connection and release all resources. */
  close(): void {
    console.log('[RealtimeSession] Closing...')

    if (this.dc) {
      try { this.dc.close() } catch {}
      this.dc = null
    }

    if (this.pc) {
      try { this.pc.close() } catch {}
      this.pc = null
    }

    if (this.micStream) {
      for (const track of this.micStream.getTracks()) {
        track.stop()
      }
      this.micStream = null
    }

    this._remoteAudioStream = null

    if (this._connected) {
      this._connected = false
      this.onDisconnected?.()
    }
  }

  // -----------------------------------------------------------------------
  // Private — Peer connection
  // -----------------------------------------------------------------------

  private setupPeerConnectionHandlers(): void {
    if (!this.pc) return

    // Remote audio track
    this.pc.ontrack = (e) => {
      console.log('[RealtimeSession] Remote audio track received')
      this._remoteAudioStream = e.streams[0] ?? new MediaStream([e.track])
      this.onRemoteTrack?.(this._remoteAudioStream)
    }

    // Connection state
    this.pc.onconnectionstatechange = () => {
      const state = this.pc?.connectionState
      console.log('[RealtimeSession] Connection state:', state)

      if (state === 'connected') {
        this._connected = true
        this.onConnected?.()
      } else if (state === 'failed' || state === 'closed' || state === 'disconnected') {
        if (this._connected) {
          this._connected = false
          this.onDisconnected?.()
        }
        if (state === 'failed') {
          this.onError?.(new Error('WebRTC connection failed'))
        }
      }
    }
  }

  private waitForIceGathering(): Promise<void> {
    if (!this.pc || this.pc.iceGatheringState === 'complete') {
      return Promise.resolve()
    }

    return new Promise<void>((resolve) => {
      const pc = this.pc!
      let timer: ReturnType<typeof setTimeout>

      const done = () => {
        clearTimeout(timer)
        try { pc.removeEventListener('icegatheringstatechange', onState) } catch {}
        resolve()
      }

      const onState = () => {
        if (pc.iceGatheringState === 'complete') done()
      }

      pc.addEventListener('icegatheringstatechange', onState)
      timer = setTimeout(done, ICE_GATHER_TIMEOUT_MS)
    })
  }

  private async exchangeSDP(localSdp: string): Promise<string> {
    const { webrtcUrl, ephemeralKey, model, voice } = this.opts

    const url = `${webrtcUrl}?model=${encodeURIComponent(model)}&voice=${encodeURIComponent(voice)}`

    const res = await fetch(url, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${ephemeralKey}`,
        'Content-Type': 'application/sdp',
        'Accept': 'application/sdp',
      },
      body: localSdp,
    })

    if (!res.ok) {
      const text = await res.text().catch(() => '')
      throw new Error(`SDP exchange failed (${res.status}): ${text}`)
    }

    return res.text()
  }

  // -----------------------------------------------------------------------
  // Private — Data channel
  // -----------------------------------------------------------------------

  private setupDataChannelHandlers(): void {
    if (!this.dc) return

    this.dc.onopen = () => {
      console.log('[RealtimeSession] Data channel open')
      this.sendSessionUpdate()
      // Trigger the AI's opening greeting
      this.sendJSON({ type: 'response.create' })
    }

    this.dc.onmessage = (e) => {
      try {
        const msg = JSON.parse(e.data)
        this.onDataMessage?.(msg)
      } catch (err) {
        console.warn('[RealtimeSession] Failed to parse data channel message:', err)
      }
    }

    this.dc.onclose = () => {
      console.log('[RealtimeSession] Data channel closed')
    }

    this.dc.onerror = (e) => {
      console.error('[RealtimeSession] Data channel error:', e)
    }
  }

  private sendSessionUpdate(): void {
    const session: Record<string, any> = {
      instructions: this.opts.instructions,
      modalities: ['audio', 'text'],
      input_audio_transcription: { model: 'whisper-1' },
      turn_detection: {
        type: 'server_vad',
        threshold: 0.5,
        prefix_padding_ms: 300,
        silence_duration_ms: 700,
        create_response: true,
      },
    }

    if (this.opts.tools && this.opts.tools.length > 0) {
      session.tools = this.opts.tools
    }

    this.sendJSON({ type: 'session.update', session })
  }
}
